import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Chip,
    Container,
    Button,
    TextField,
    MenuItem,
    Grid,
    LinearProgress
} from "@mui/material";
import {
    ArrowBack,
    Download,
    EmojiEvents
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../AdminLayout";
import learningService from "../../services/learningService";

export default function LearningPathScoreboardPage() {
    const navigate = useNavigate();
    const { pathId } = useParams();
    const [path, setPath] = useState(null);
    const [progresses, setProgresses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [levelFilter, setLevelFilter] = useState("all");

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const pathData = await learningService.getLearningPathById(pathId);
                setPath(pathData);

                const progressList = await learningService.getPathProgressList(pathId);
                setProgresses(progressList);
            } catch (error) {
                console.error("Error fetching scoreboard data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [pathId]);

    const getProgressByLevel = (progress) => {
        const levels = path?.levels || [];
        const levelStatuses = progress.levelStatus || {};
        const clearedLevels = Object.values(levelStatuses).filter((s) => s.cleared).length;
        return (clearedLevels / levels.length) * 100;
    };

    const getSortedProgresses = () => {
        return progresses
            .map((p) => ({
                ...p,
                progressPercentage: getProgressByLevel(p),
                creditsEarned: p.totalCreditsEarned || 0
            }))
            .sort((a, b) => b.progressPercentage - a.progressPercentage);
    };

    const downloadScoreboard = () => {
        const headers = ["Rank", "Name", "Email", "Progress", "Credits Earned", "Paths Completed"];
        const rows = getSortedProgresses().map((p, index) => [
            index + 1,
            p.userName || "Unknown",
            p.userEmail || "N/A",
            `${Math.round(p.progressPercentage)}%`,
            p.creditsEarned,
            p.pathCompleted ? "Yes" : "No"
        ]);

        const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${path?.name}-scoreboard.csv`;
        a.click();
    };

    if (loading) {
        return (
            <AdminLayout title="Learning Path Scoreboard">
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "400px" }}>
                    <CircularProgress />
                </Box>
            </AdminLayout>
        );
    }

    if (!path) {
        return (
            <AdminLayout title="Learning Path Scoreboard">
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">Learning path not found</Alert>
                </Box>
            </AdminLayout>
        );
    }

    const sortedProgresses = getSortedProgresses();

    return (
        <AdminLayout title="Learning Path Scoreboard">
            <Container maxWidth="lg" sx={{ py: 3 }}>
                {/* Header */}
                <Box sx={{ mb: 3 }}>
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate("/admin/learning-paths")}
                        sx={{ mb: 2 }}
                    >
                        Back to Learning Paths
                    </Button>

                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                                {path.name} - Scoreboard
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Student progress and performance tracking
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            startIcon={<Download />}
                            onClick={downloadScoreboard}
                        >
                            Download CSV
                        </Button>
                    </Box>
                </Box>

                {/* Stats */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="caption" color="text.secondary">
                                    Total Enrolled
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                    {progresses.length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="caption" color="text.secondary">
                                    Completed
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: "success.main" }}>
                                    {progresses.filter((p) => p.pathCompleted).length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="caption" color="text.secondary">
                                    In Progress
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: "warning.main" }}>
                                    {progresses.filter((p) => !p.pathCompleted && getProgressByLevel(p) > 0).length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="caption" color="text.secondary">
                                    Not Started
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 700, color: "error.main" }}>
                                    {progresses.filter((p) => getProgressByLevel(p) === 0).length}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Scoreboard Table */}
                <Card>
                    <CardContent>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                Ranking
                            </Typography>
                        </Box>

                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead sx={{ backgroundColor: "primary.lighter" }}>
                                    <TableRow>
                                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                                            Rank
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Student Name</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            Progress
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            Credits Earned
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                                            Status
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedProgresses.map((progress, index) => (
                                        <TableRow
                                            key={progress.id}
                                            hover
                                            sx={{
                                                backgroundColor:
                                                    index === 0
                                                        ? "warning.lighter"
                                                        : index === 1
                                                            ? "info.lighter"
                                                            : index === 2
                                                                ? "success.lighter"
                                                                : "inherit"
                                            }}
                                        >
                                            <TableCell align="center">
                                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                    {index < 3 ? (
                                                        <EmojiEvents
                                                            sx={{
                                                                color:
                                                                    index === 0
                                                                        ? "#FFD700"
                                                                        : index === 1
                                                                            ? "#C0C0C0"
                                                                            : "#CD7F32"
                                                            }}
                                                        />
                                                    ) : (
                                                        <Typography sx={{ fontWeight: 700 }}>
                                                            {index + 1}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{progress.userName || "Unknown"}</TableCell>
                                            <TableCell>{progress.userEmail || "N/A"}</TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={progress.progressPercentage}
                                                        sx={{ flex: 1, height: 6 }}
                                                    />
                                                    <Typography variant="caption" sx={{ minWidth: 40, fontWeight: 700 }}>
                                                        {Math.round(progress.progressPercentage)}%
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Chip
                                                    label={`${progress.creditsEarned} pts`}
                                                    color="warning"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={
                                                        progress.pathCompleted
                                                            ? "Completed"
                                                            : progress.progressPercentage > 0
                                                                ? "In Progress"
                                                                : "Not Started"
                                                    }
                                                    color={
                                                        progress.pathCompleted
                                                            ? "success"
                                                            : progress.progressPercentage > 0
                                                                ? "warning"
                                                                : "default"
                                                    }
                                                    size="small"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Container>
        </AdminLayout>
    );
}
